import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ActionButton from '../../../components/ActionButton';
import { makeStyles } from '@material-ui/styles';
import ModalResponsabiliPerReparto from '../manualecomponents/paragrafo4/ModalResponsabiliPerReparto';
import { getResponsabiliSicurezzaEsercizio } from '../../../utils/api/responsabili_sicurezza_api';
import ModalCronologiaAggiornamenti from './ModalCronologiaAggiornamenti';
import ModalSelezioneModalitaAutocontrollo from '../manuale_personalizzato/ModalSelezioneModalitaAutocontrollo';
import {
  getControlliSenzaAttrezzatureGestioneOperativa,
  getGestioneOperativaLocali,
  getGestioneOperativaProcedure,
  getGestioneOperativaReparti,
  getGestioneOperativaSchede,
  getGestioneOperativaStorico,
  getGestioneOperativaStoricoPdf,
  pubblicaGestioneOperativa,
  replicaGestioneOperativa,
  updateGestioneOperativaLocali,
  updateGestioneOperativaProcedure,
  updateGestioneOperativaReparti,
  updateGestioneOperativaSchede,
} from '../../../utils/api/aggiornamentoGestioneOperativaApi';
import SelezioneAttrezzatureModal from './SelezioneAttrezzatureModal';
import ModalAvvisoControlliConformita from './ModalAvvisoControlliConformita';
import IndietroButton from '../../../components/IndietroButton';
import ElencoRepartiTable from './ElencoRepartiTable';
import DescrizioneRepartiTable from './DescrizioneRepartiTable';
import ProcedureAutocontrolloTable from './ProcedureAutocontrolloTable';
import CustomSnackbar from '../../../components/CustomSnackbar';
import SwitchSection from '../dettaglio_manuale/SwitchSection';
import HttpError from '../../../utils/HttpError';
import SectionBlockerWrapper from '../components/SectionBlockerWrapper';
import { Paper } from '@material-ui/core';
import {
  cleanEntityIds,
  generateTempId,
  isTempId,
} from '../../../utils/idHelpers';
import { skipNextNavigationGuard } from '../../../utils/useUnsavedChangesNavigationGuard';
import { UNSAVED_CHANGES_LEAVE_MESSAGE } from '../../../utils/messages';
import DialogConfirmOperation from '../components/DialogConfirmOperation';
import AvvisoCodaRevisione from './iot/AvvisoCodaRevisione';
import BannerConfigurazioneHaccp from './BannerConfigurazioneHaccp';
import ModalImportHaccpAgent from './import_haccp/ModalImportHaccpAgent';
import importHaccpAgentBackend from './import_haccp/importHaccpAgentBackend';
import ModalSelezioneEserciziCommerciali from '../components/ModalSelezioneEserciziCommerciali';
import { reportApiError, reportError } from '../../../utils/reportApiError';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(4),
    margin: theme.spacing(4, 0),
    width: '85%',
    maxWidth: '1600px',
    [theme.breakpoints.down('md')]: {
      width: '95%',
    },
  },
  // Same width, horizontal padding and box model as `sectionContainer`, so the
  // two cards share their edges.
  bannerContainer: {
    width: '100%',
    padding: theme.spacing(3, 4),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: theme.spacing(4),
  },
  indietroButton: {
    width: '100%',
    textAlign: 'right',
  },
}));

const toProcedureHaccpDraft = (procedureHaccp) =>
  procedureHaccp.map((procedura) => ({
    ...procedura,
    isScheda: false,
  }));

const toSchedeAutocontrolloDraft = (schede) =>
  schede.map((scheda) => ({
    ...scheda,
    isScheda: true,
  }));

const combineProcedureLists = (
  procedureHaccpDraft,
  schedeAutocontrolloDraft,
) => [...procedureHaccpDraft, ...schedeAutocontrolloDraft];

const serializeCollection = (collection) => JSON.stringify(collection);

const hasCollectionChanges = (current, saved) =>
  serializeCollection(current) !== serializeCollection(saved);

const mergeLocaliWithDrafts = (apiLocali, localLocali, removedTipologiaId) => {
  const isLinkedToRemoved = (attrezzatura) =>
    removedTipologiaId != null &&
    attrezzatura.tipologiaStrumentazioneId === removedTipologiaId;

  const mergedPersisted = apiLocali.map((apiLocale) => {
    const localLocale = localLocali.find(
      (locale) => locale.id === apiLocale.id,
    );
    const draftAttrezzature = (localLocale?.attrezzature || []).filter(
      (attrezzatura) =>
        attrezzatura.id == null && !isLinkedToRemoved(attrezzatura),
    );
    return {
      ...apiLocale,
      attrezzature: [...(apiLocale.attrezzature || []), ...draftAttrezzature],
    };
  });

  const draftLocali = localLocali
    .filter((locale) => isTempId(locale.id))
    .map((locale) => ({
      ...locale,
      attrezzature: (locale.attrezzature || []).filter(
        (attrezzatura) => !isLinkedToRemoved(attrezzatura),
      ),
    }));

  return [...mergedPersisted, ...draftLocali];
};

const getProcedureDraftChanges = (
  procedureHaccpDraft,
  schedeAutocontrolloDraft,
  procedureHaccp,
  schedeAutocontrollo,
  isEditingDisabledForConsulente,
  initialIsEditingDisabledForConsulente,
) => {
  const updatedProcedureHaccp = procedureHaccpDraft.map((item, index) => ({
    ...item,
    numeroComponente: index,
  }));
  const updatedSchede = schedeAutocontrolloDraft.map((item, index) => ({
    ...item,
    numeroComponente: index,
  }));

  const isBloccatoChangedProcedureHaccp =
    isEditingDisabledForConsulente.procedureHaccp !==
    initialIsEditingDisabledForConsulente.procedureHaccp;
  const isBloccatoChangedSchede =
    isEditingDisabledForConsulente.schedeAutocontrollo !==
    initialIsEditingDisabledForConsulente.schedeAutocontrollo;

  const hasChangedProcedureHaccp =
    updatedProcedureHaccp.length !== procedureHaccp.length ||
    updatedProcedureHaccp.some((updated) => {
      const original = procedureHaccp.find((p) => p.id === updated.id);
      return (
        !original ||
        updated.titolo !== original.titolo ||
        updated.numVersione !== original.numVersione ||
        updated.dataVersione !== original.dataVersione
      );
    }) ||
    isBloccatoChangedProcedureHaccp;

  const hasChangedSchede =
    updatedSchede.length !== schedeAutocontrollo.length ||
    updatedSchede.some((updated) => {
      const original = schedeAutocontrollo.find((s) => s.id === updated.id);
      return (
        !original ||
        updated.titolo !== original.titolo ||
        updated.numVersione !== original.numVersione ||
        updated.dataVersione !== original.dataVersione
      );
    }) ||
    isBloccatoChangedSchede;

  return {
    hasChangedProcedureHaccp,
    hasChangedSchede,
    updatedProcedureHaccp,
    updatedSchede,
  };
};

const GestioneOperativa = ({
  aggiornamentoGestioneOperativaDTO,
  checkboxSaveSuccess,
  codaRicaricaToken,
  esercizioId,
  loadingDatiEsercizio,
  mostraCodaRevisione,
  onApriCodaRevisione,
  onCodaCambiata,
  onChangeCheckbox,
  onPendingChangesChange,
  onSaveSwitchSettings,
}) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [elencoReparti, setElencoReparti] = useState([]);
  const [savedElencoReparti, setSavedElencoReparti] = useState([]);
  const [descrizioneReparti, setDescrizioneReparti] = useState([]);
  const [savedDescrizioneReparti, setSavedDescrizioneReparti] = useState([]);
  const [procedureHaccpDraft, setProcedureHaccpDraft] = useState([]);
  const [schedeAutocontrolloDraft, setSchedeAutocontrolloDraft] = useState([]);
  const [procedureHaccp, setProcedureHaccp] = useState([]);
  const [schedeAutocontrollo, setSchedeAutocontrollo] = useState([]);

  const procedureAutocontrollo = useMemo(
    () => combineProcedureLists(procedureHaccpDraft, schedeAutocontrolloDraft),
    [procedureHaccpDraft, schedeAutocontrolloDraft],
  );

  const [activeSection, setActiveSection] = useState(null);

  const [isModalResponsabiliOpen, setIsModalResponsabiliOpen] = useState(false);
  const [selectedReparto, setSelectedReparto] = useState({});
  const [isAttrezzatureModalOpen, setIsAttrezzatureModalOpen] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState({});
  const [responsabiliSicurezza, setResponsabiliSicurezza] = useState([]);
  const [confirmRemoveLocale, setConfirmRemoveLocale] = useState({
    open: false,
    rowIndex: null,
  });
  const [confirmRemoveReparto, setConfirmRemoveReparto] = useState({
    open: false,
    rowIndex: null,
    repartoNome: '',
  });
  const [
    isModalSelezioneModalitaAutocontrolloOpen,
    setIsModalSelezioneModalitaAutocontrolloOpen,
  ] = useState(false);
  const [isModalCronologiaOpen, setIsModalCronologiaOpen] = useState(false);
  const [isLoadingCronologia, setIsLoadingCronologia] = useState(false);
  const [isPubblicaDisabled, setIsPubblicaDisabled] = useState(true);
  const [storicoAggiornamenti, setStoricoAggiornamenti] = useState([]);
  const [isEditingDisabledForConsulente, setIsEditingDisabledForConsulente] =
    useState({
      elencoReparti: false,
      descrizioneReparti: false,
      procedureHaccp: false,
      schedeAutocontrollo: false,
    });
  const [
    initialIsEditingDisabledForConsulente,
    setInitialIsEditingDisabledForConsulente,
  ] = useState({
    procedureHaccp: false,
    schedeAutocontrollo: false,
  });

  const hasPendingChanges = useMemo(() => {
    const { hasChangedProcedureHaccp, hasChangedSchede } =
      getProcedureDraftChanges(
        procedureHaccpDraft,
        schedeAutocontrolloDraft,
        procedureHaccp,
        schedeAutocontrollo,
        isEditingDisabledForConsulente,
        initialIsEditingDisabledForConsulente,
      );

    return (
      hasCollectionChanges(elencoReparti, savedElencoReparti) ||
      hasCollectionChanges(descrizioneReparti, savedDescrizioneReparti) ||
      hasChangedProcedureHaccp ||
      hasChangedSchede
    );
  }, [
    descrizioneReparti,
    elencoReparti,
    initialIsEditingDisabledForConsulente,
    isEditingDisabledForConsulente,
    procedureHaccp,
    procedureHaccpDraft,
    savedDescrizioneReparti,
    savedElencoReparti,
    schedeAutocontrollo,
    schedeAutocontrolloDraft,
  ]);

  useEffect(() => {
    if (!hasPendingChanges) {
      setActiveSection(null);
    }
  }, [hasPendingChanges]);

  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges);

    return () => {
      onPendingChangesChange?.(false);
    };
  }, [hasPendingChanges, onPendingChangesChange]);

  useEffect(() => {
    if (!hasPendingChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_LEAVE_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingChanges]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });
  const [isModalEserciziOpen, setIsModalEserciziOpen] = useState(false);
  const [controlliSenzaAttrezzature, setControlliSenzaAttrezzature] = useState(
    [],
  );
  const [isModalControlliOpen, setIsModalControlliOpen] = useState(false);
  const [pendingActionAfterCheck, setPendingActionAfterCheck] = useState(null);
  const [isImportHaccpOpen, setIsImportHaccpOpen] = useState(false);
  // Bumped after an import, so the banner reads the import again.
  const [importHaccpToken, setImportHaccpToken] = useState(0);

  const openSuccessDialog = (message) => {
    reportError(message, 'Operazione effettuata');
  };

  useEffect(() => {
    let isMounted = true;

    getResponsabiliSicurezzaEsercizio(esercizioId)
      .then((responsabili) => {
        if (isMounted) {
          setResponsabiliSicurezza(responsabili);
        }
      })
      .catch((error) => {
        if (isMounted) {
          reportApiError(error);
        }
      });

    return () => {
      isMounted = false; // Cleanup: evita aggiornamenti di stato su componenti smontati
    };
  }, [esercizioId]);

  const getRepartiData = useCallback(() => {
    if (!aggiornamentoGestioneOperativaDTO.id) return;

    getGestioneOperativaReparti(aggiornamentoGestioneOperativaDTO.id)
      .then((response) => {
        setElencoReparti(response.reparti);
        setSavedElencoReparti(response.reparti);
        setIsEditingDisabledForConsulente((prev) => ({
          ...prev,
          elencoReparti: response.bloccato,
        }));
      })
      .catch((error) => {
        reportApiError(error);
      });
  }, [aggiornamentoGestioneOperativaDTO.id]);

  const getLocaliData = (removedTipologiaId = null) => {
    if (!aggiornamentoGestioneOperativaDTO.id) return Promise.resolve();

    return getGestioneOperativaLocali(aggiornamentoGestioneOperativaDTO.id)
      .then((response) => {
        const apiLocali = response.locali || [];
        setDescrizioneReparti((prev) =>
          mergeLocaliWithDrafts(apiLocali, prev, removedTipologiaId),
        );
        setSavedDescrizioneReparti(apiLocali);
        setIsEditingDisabledForConsulente((prev) => ({
          ...prev,
          descrizioneReparti: response.bloccato,
        }));
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  useEffect(() => {
    getRepartiData();
  }, [aggiornamentoGestioneOperativaDTO.id, getRepartiData]);

  useEffect(() => {
    let isMounted = true;
    if (!aggiornamentoGestioneOperativaDTO.id) return;

    getGestioneOperativaLocali(aggiornamentoGestioneOperativaDTO.id)
      .then((response) => {
        if (isMounted) {
          setDescrizioneReparti(response.locali);
          setSavedDescrizioneReparti(response.locali);
          setIsEditingDisabledForConsulente((prev) => ({
            ...prev,
            descrizioneReparti: response.bloccato,
          }));
        }
      })
      .catch((error) => {
        if (isMounted) {
          reportApiError(error);
        }
      });
  }, [aggiornamentoGestioneOperativaDTO.id]);

  useEffect(() => {
    let isMounted = true;

    if (!aggiornamentoGestioneOperativaDTO.id) return;

    Promise.all([
      getGestioneOperativaProcedure(aggiornamentoGestioneOperativaDTO.id),
      getGestioneOperativaSchede(aggiornamentoGestioneOperativaDTO.id),
    ])
      .then(([procedureResponse, schedeResponse]) => {
        if (isMounted) {
          const procedureData = toProcedureHaccpDraft(
            procedureResponse.procedureHaccp,
          );
          const schedeData = toSchedeAutocontrolloDraft(schedeResponse.schede);

          setProcedureHaccpDraft(procedureData);
          setSchedeAutocontrolloDraft(schedeData);
          setIsEditingDisabledForConsulente((prev) => ({
            ...prev,
            procedureHaccp: procedureResponse.bloccato,
            schedeAutocontrollo: schedeResponse.bloccato,
          }));
          setInitialIsEditingDisabledForConsulente({
            procedureHaccp: procedureResponse.bloccato,
            schedeAutocontrollo: schedeResponse.bloccato,
          });
          setProcedureHaccp(procedureData);
          setSchedeAutocontrollo(schedeData);
        }
      })
      .catch((error) => {
        if (isMounted) {
          reportApiError(error);
        }
      })
      .finally(() => setIsLoading(false));

    return () => {
      isMounted = false;
    };
  }, [aggiornamentoGestioneOperativaDTO.id]);

  const refreshAllData = async () => {
    if (!aggiornamentoGestioneOperativaDTO.id) return;

    setIsLoading(true);
    try {
      // Reparti
      await getRepartiData();
      // Locali
      const localiResponse = await getGestioneOperativaLocali(
        aggiornamentoGestioneOperativaDTO.id,
      );
      setDescrizioneReparti(localiResponse.locali);
      setSavedDescrizioneReparti(localiResponse.locali);
      setIsEditingDisabledForConsulente((prev) => ({
        ...prev,
        descrizioneReparti: localiResponse.bloccato,
      }));
      // Procedure e schede
      const [procedureResponse, schedeResponse] = await Promise.all([
        getGestioneOperativaProcedure(aggiornamentoGestioneOperativaDTO.id),
        getGestioneOperativaSchede(aggiornamentoGestioneOperativaDTO.id),
      ]);
      const procedureData = toProcedureHaccpDraft(
        procedureResponse.procedureHaccp,
      );
      const schedeData = toSchedeAutocontrolloDraft(schedeResponse.schede);
      setProcedureHaccpDraft(procedureData);
      setSchedeAutocontrolloDraft(schedeData);
      setProcedureHaccp(procedureData);
      setSchedeAutocontrollo(schedeData);
      setIsEditingDisabledForConsulente((prev) => ({
        ...prev,
        procedureHaccp: procedureResponse.bloccato,
        schedeAutocontrollo: schedeResponse.bloccato,
      }));
      setInitialIsEditingDisabledForConsulente({
        procedureHaccp: procedureResponse.bloccato,
        schedeAutocontrollo: schedeResponse.bloccato,
      });
      setActiveSection(null);
    } catch (error) {
      reportApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // An import replaces the whole configuration: every section is read again,
  // and the HACCP Agent banner with them.
  const ricaricaDopoImport = () => {
    setImportHaccpToken((token) => token + 1);
    refreshAllData();
  };

  const addNewRow = async (section) => {
    switch (section) {
      case 'elencoReparti': {
        const newElencoRepartiRow = {
          id: generateTempId(),
          nome: 'Nuovo reparto',
          repartoDefault: false,
          responsabili: [],
        };

        setElencoReparti((prev) => [...prev, newElencoRepartiRow]);

        setActiveSection(section);
        setIsPubblicaDisabled(false);
        break;
      }
      case 'descrizioneReparti': {
        const newDescrizioneRepartiRow = {
          id: generateTempId(),
          idGestioneOperativa: aggiornamentoGestioneOperativaDTO.id,
          repartoId: elencoReparti[0]?.id,
          nome: 'Nuovo locale',
          dimensione: 0,
          indiceOrdinamento: descrizioneReparti?.length || 0,
          attrezzature: [],
        };

        setDescrizioneReparti((prev) => [...prev, newDescrizioneRepartiRow]);

        setActiveSection(section);
        setIsPubblicaDisabled(false);

        break;
      }
      case 'procedureAutocontrollo':
        setIsModalSelezioneModalitaAutocontrolloOpen(true);
        setIsPubblicaDisabled(false);
        break;
      default:
        return;
    }
  };

  const handleRepartiCellChange = (rowIndex, fieldName, value) => {
    setElencoReparti((prev) =>
      prev.map((reparto, index) =>
        index === rowIndex ? { ...reparto, [fieldName]: value } : reparto,
      ),
    );

    setIsPubblicaDisabled(false);
    setActiveSection('elencoReparti');
  };

  const handleLocaliCellChange = (rowIndex, fieldName, value) => {
    setDescrizioneReparti((prev) =>
      prev.map((locale, index) =>
        index === rowIndex ? { ...locale, [fieldName]: value } : locale,
      ),
    );

    setIsPubblicaDisabled(false);
    setActiveSection('descrizioneReparti');
  };

  const handleProcedureCellChange = (rowIndex, fieldName, value) => {
    const procedureHaccpCount = procedureHaccpDraft.length;

    if (rowIndex < procedureHaccpCount) {
      setProcedureHaccpDraft((prev) =>
        prev.map((item, index) =>
          index === rowIndex ? { ...item, [fieldName]: value } : item,
        ),
      );
    } else {
      const schedaIndex = rowIndex - procedureHaccpCount;
      setSchedeAutocontrolloDraft((prev) =>
        prev.map((item, index) =>
          index === schedaIndex ? { ...item, [fieldName]: value } : item,
        ),
      );
    }

    setIsPubblicaDisabled(false);
    setActiveSection('procedureAutocontrollo');
  };

  const onClickAssociaResponsabili = (reparto) => {
    setIsModalResponsabiliOpen(true);
    setSelectedReparto(reparto);
  };

  const onCloseModalResponsabili = () => {
    setIsModalResponsabiliOpen(false);
    setSelectedReparto(null);
  };

  const onResponsabileSelected = (responsabiliIds) => {
    setElencoReparti((prev) =>
      prev.map((reparto) =>
        reparto.id === selectedReparto.id
          ? { ...reparto, responsabili: responsabiliIds }
          : reparto,
      ),
    );
    setIsPubblicaDisabled(false);
    setActiveSection('elencoReparti');

    onCloseModalResponsabili();
  };

  const getResponsabiliEmailFromId = (responsabiliIds) => {
    return responsabiliIds
      .map(
        (id) =>
          responsabiliSicurezza.find((responsabile) => +responsabile.id === +id)
            ?.email,
      )
      .filter(Boolean) // Rimuove valori null/undefined
      .join(', ');
  };

  const removeRepartoRow = (rowIndex) => {
    const removedRepartoId = elencoReparti[rowIndex]?.id;

    setElencoReparti((prev) =>
      prev.filter((_row, index) => index !== rowIndex),
    );
    setDescrizioneReparti((prev) =>
      prev.filter((locale) => locale.repartoId !== removedRepartoId),
    );
    setActiveSection('elencoReparti');

    setIsPubblicaDisabled(false);
  };

  const onRemoveRowReparti = (cellInfo) => {
    const reparto = elencoReparti[cellInfo.index];
    const localiCollegati = descrizioneReparti.filter(
      (locale) => locale.repartoId === reparto?.id,
    );

    if (localiCollegati.length > 0) {
      setConfirmRemoveReparto({
        open: true,
        rowIndex: cellInfo.index,
        repartoNome: reparto?.nome ?? '',
      });
      return;
    }

    removeRepartoRow(cellInfo.index);
  };

  const handleConfirmRemoveReparto = () => {
    removeRepartoRow(confirmRemoveReparto.rowIndex);
    setConfirmRemoveReparto({
      open: false,
      rowIndex: null,
      repartoNome: '',
    });
  };

  const handleCancelRemoveReparto = () => {
    setConfirmRemoveReparto({
      open: false,
      rowIndex: null,
      repartoNome: '',
    });
  };

  const onClickAttrezzature = (locale, index) => {
    setSelectedLocale(locale);
    setIsAttrezzatureModalOpen(true);
  };

  const onCloseAttrezzatureModal = () => setIsAttrezzatureModalOpen(false);

  const onCloseModalEsercizi = () => setIsModalEserciziOpen(false);

  const onRemoveRowDescrizioneReparti = (cellInfo) => {
    setConfirmRemoveLocale({
      open: true,
      rowIndex: cellInfo.index,
    });
  };

  const handleConfirmRemoveLocale = () => {
    setDescrizioneReparti((prev) =>
      prev.filter((_row, index) => index !== confirmRemoveLocale.rowIndex),
    );

    setActiveSection('descrizioneReparti');
    setConfirmRemoveLocale({ open: false, rowIndex: null });
  };

  const handleCancelRemoveLocale = () => {
    setConfirmRemoveLocale({ open: false, rowIndex: null });
  };

  const handleCambioRepartoInLocale = (id, rowIndex) => {
    const selectedReparto = elencoReparti.find((reparto) => reparto.id === id);
    if (!selectedReparto) return;

    setDescrizioneReparti((prev) =>
      prev.map((locale, index) =>
        index === rowIndex ? { ...locale, repartoId: id } : locale,
      ),
    );
    setActiveSection('descrizioneReparti');
  };

  const onCloseModalSelezioneModalitaAutocontrollo = () =>
    setIsModalSelezioneModalitaAutocontrolloOpen(false);

  const onRemoveRowProcedure = (cellInfo) => {
    const procedureHaccpCount = procedureHaccpDraft.length;

    if (cellInfo.index < procedureHaccpCount) {
      setProcedureHaccpDraft((prev) =>
        prev.filter((_row, index) => index !== cellInfo.index),
      );
    } else {
      const schedaIndex = cellInfo.index - procedureHaccpCount;
      setSchedeAutocontrolloDraft((prev) =>
        prev.filter((_row, index) => index !== schedaIndex),
      );
    }

    setActiveSection('procedureAutocontrollo');
    setIsPubblicaDisabled(false);
  };

  const handleNewRowProcedureAutocontrollo = (tipologia) => {
    const isScheda = tipologia === 'Scheda';
    const nuovaRiga = {
      id: generateTempId(),
      titolo: isScheda ? 'Nuova scheda' : 'Nuova procedura',
      numVersione: '',
      dataVersione: '',
      isScheda,
    };

    if (isScheda) {
      setSchedeAutocontrolloDraft((prev) => [...prev, nuovaRiga]);
    } else {
      setProcedureHaccpDraft((prev) => [...prev, nuovaRiga]);
    }

    setIsModalSelezioneModalitaAutocontrolloOpen(false);
    setActiveSection('procedureAutocontrollo');
    setIsPubblicaDisabled(false);
  };

  const handleSaveReparti = async () => {
    setIsLoading(true);

    const repartiDaSalvare = cleanEntityIds(elencoReparti);
    const localiChanged = hasCollectionChanges(
      descrizioneReparti,
      savedDescrizioneReparti,
    );

    try {
      await updateGestioneOperativaReparti(
        aggiornamentoGestioneOperativaDTO.id,
        {
          idGestioneOperativa: aggiornamentoGestioneOperativaDTO.id,
          bloccato: isEditingDisabledForConsulente.elencoReparti,
          reparti: repartiDaSalvare,
        },
      );

      if (localiChanged) {
        await updateGestioneOperativaLocali(
          aggiornamentoGestioneOperativaDTO.id,
          {
            idGestioneOperativa: aggiornamentoGestioneOperativaDTO.id,
            bloccato: isEditingDisabledForConsulente.descrizioneReparti,
            locali: cleanEntityIds(descrizioneReparti),
          },
        );

        const refetchedLocali = await getGestioneOperativaLocali(
          aggiornamentoGestioneOperativaDTO.id,
        );
        setDescrizioneReparti(refetchedLocali.locali);
        setSavedDescrizioneReparti(refetchedLocali.locali);
        setIsEditingDisabledForConsulente((prev) => ({
          ...prev,
          descrizioneReparti: refetchedLocali.bloccato,
        }));
      }

      handleOpenSnackbar('Reparti salvati con successo!');
      setActiveSection(null);
      getRepartiData();
    } catch (error) {
      reportApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLocali = async () => {
    setIsLoading(true);
    try {
      const localiDaSalvare = cleanEntityIds(descrizioneReparti);

      const response = await updateGestioneOperativaLocali(
        aggiornamentoGestioneOperativaDTO.id,
        {
          idGestioneOperativa: aggiornamentoGestioneOperativaDTO.id,
          bloccato: isEditingDisabledForConsulente.descrizioneReparti,
          locali: localiDaSalvare,
        },
      );

      if (response?.locali) {
        const refetched = await getGestioneOperativaLocali(
          aggiornamentoGestioneOperativaDTO.id,
        );
        setDescrizioneReparti(refetched.locali);
        setSavedDescrizioneReparti(refetched.locali);

        setIsEditingDisabledForConsulente((prev) => ({
          ...prev,
          descrizioneReparti: refetched.bloccato,
        }));
        handleOpenSnackbar('Locali salvati con successo!');
        setActiveSection(null);
      }
    } catch (error) {
      reportApiError(error);
    } finally {
      setIsLoading(false);
    }
    setIsPubblicaDisabled(false);
  };

  const saveAttrezzatureLocale = (updatedLocale) => {
    setDescrizioneReparti((prev) =>
      prev.map((locale) =>
        locale.id === updatedLocale.id ? updatedLocale : locale,
      ),
    );
    setActiveSection('descrizioneReparti');

    setIsPubblicaDisabled(false);
  };

  const handleSaveProcedure = async () => {
    setIsLoading(true);

    try {
      const {
        hasChangedProcedureHaccp,
        hasChangedSchede,
        updatedProcedureHaccp,
        updatedSchede,
      } = getProcedureDraftChanges(
        procedureHaccpDraft,
        schedeAutocontrolloDraft,
        procedureHaccp,
        schedeAutocontrollo,
        isEditingDisabledForConsulente,
        initialIsEditingDisabledForConsulente,
      );

      // Verifica degli elementi nuovi (quelli con ID temporaneo)
      const nuoveProcedure = updatedProcedureHaccp.filter((p) =>
        p.id.toString().startsWith('temp-'),
      );
      const nuoveSchede = updatedSchede.filter((s) =>
        s.id.toString().startsWith('temp-'),
      );

      // Effettuiamo solo le PUT necessarie
      if (hasChangedProcedureHaccp || nuoveProcedure.length > 0) {
        await updateGestioneOperativaProcedure(
          aggiornamentoGestioneOperativaDTO.id,
          {
            bloccato: isEditingDisabledForConsulente.procedureHaccp,
            procedureHaccp: cleanEntityIds(updatedProcedureHaccp),
          },
        );
      }

      if (hasChangedSchede || nuoveSchede.length > 0) {
        await updateGestioneOperativaSchede(
          aggiornamentoGestioneOperativaDTO.id,
          {
            bloccato: isEditingDisabledForConsulente.schedeAutocontrollo,
            schede: cleanEntityIds(updatedSchede),
          },
        );
      }

      // GET per aggiornare lo stato con gli ID reali
      const [procedureResponse, schedeResponse] = await Promise.all([
        getGestioneOperativaProcedure(aggiornamentoGestioneOperativaDTO.id),
        getGestioneOperativaSchede(aggiornamentoGestioneOperativaDTO.id),
      ]);

      const procedureData = toProcedureHaccpDraft(
        procedureResponse.procedureHaccp,
      );
      const schedeData = toSchedeAutocontrolloDraft(schedeResponse.schede);

      setProcedureHaccpDraft(procedureData);
      setSchedeAutocontrolloDraft(schedeData);
      setProcedureHaccp(procedureData);
      setSchedeAutocontrollo(schedeData);
      setInitialIsEditingDisabledForConsulente({
        procedureHaccp: procedureResponse.bloccato,
        schedeAutocontrollo: schedeResponse.bloccato,
      });

      handleOpenSnackbar('Procedure salvate con successo!');
      setActiveSection(null);

      setIsPubblicaDisabled(false);
    } catch (error) {
      reportApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onReplicaAggiornamento = (ids) => {
    setIsLoading(true);
    replicaGestioneOperativa(aggiornamentoGestioneOperativaDTO.id, ids)
      .then(() => {
        openSuccessDialog('Replica avvenuta con successo');
        setIsPubblicaDisabled(true);
        refreshAllData();
      })
      .catch((error) => {
        reportApiError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onPubblicaAggiornamento = () => {
    setIsLoading(true);
    pubblicaGestioneOperativa(aggiornamentoGestioneOperativaDTO.id)
      .then(() => {
        openSuccessDialog('Pubblicazione effettuata.');
        setIsPubblicaDisabled(true);
        // The remap runs inside the publication transaction (ADR-0008), so by the
        // time this POST returns the voci are already there: nothing to wait for
        // in polling. The token lives above this component because the review tab
        // changes the same count and both tabs stay mounted.
        onCodaCambiata?.();
        refreshAllData();
      })
      .catch((error) => {
        reportApiError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const proceedAction = (action) => {
    if (action === 'pubblica') {
      onPubblicaAggiornamento();
    } else if (action === 'replica') {
      setIsModalEserciziOpen(true);
    }
  };

  const runControlliCheck = (action) => {
    setIsLoading(true);
    getControlliSenzaAttrezzatureGestioneOperativa(
      aggiornamentoGestioneOperativaDTO.id,
    )
      .then((response) => {
        const controlli = response || [];
        if (controlli.length > 0) {
          setControlliSenzaAttrezzature(controlli);
          setPendingActionAfterCheck(action);
          setIsModalControlliOpen(true);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          proceedAction(action);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        reportApiError(error);
      });
  };

  const onCloseModalControlli = () => {
    setIsModalControlliOpen(false);
    setPendingActionAfterCheck(null);
    setIsLoading(false);
  };

  const onConfirmModalControlli = () => {
    setIsModalControlliOpen(false);
    const action = pendingActionAfterCheck;
    setPendingActionAfterCheck(null);
    proceedAction(action);
  };

  const onClickCronologia = () => {
    setIsLoadingCronologia(true);
    getGestioneOperativaStorico(esercizioId, 0, 100)
      .then((response) => {
        setStoricoAggiornamenti(response.list);
        setIsModalCronologiaOpen(true);
      })
      .catch((error) => {
        reportApiError(error);
      })
      .finally(() => {
        setIsLoadingCronologia(false);
      });
  };

  const onCloseModalCronologia = () => setIsModalCronologiaOpen(false);

  const handleOpenSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const onToggleEditingForConsulente = (name) =>
    setIsEditingDisabledForConsulente((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));

  const dowloadPdf = (id) => {
    getGestioneOperativaStoricoPdf(id)
      .then((response) => {
        if (!response.ok) {
          throw new HttpError('Errore nel download del PDF', response.status);
        }
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((error) => {
        reportApiError(error);
      });
  };

  return (
    <div className={classes.mainContainer}>
      <div className={classes.contentWrapper}>
        <Paper
          elevation={3}
          component="section"
          aria-label="Configurazione da HACCP Agent"
          className={classes.bannerContainer}
        >
          <SectionBlockerWrapper
            blocked={Boolean(activeSection)}
            showMessage={!isLoading}
          >
            <BannerConfigurazioneHaccp
              aggiornamentoGestioneOperativaId={
                aggiornamentoGestioneOperativaDTO?.id
              }
              onImporta={() => setIsImportHaccpOpen(true)}
              ricaricaToken={importHaccpToken}
            />
          </SectionBlockerWrapper>
        </Paper>
        <Paper elevation={3} className={classes.sectionContainer}>
          <SectionBlockerWrapper
            blocked={Boolean(
              activeSection && activeSection !== 'elencoReparti',
            )}
            showMessage={!isLoading}
          >
            <ElencoRepartiTable
              addNewRow={addNewRow}
              elencoReparti={elencoReparti}
              getResponsabiliEmailFromId={getResponsabiliEmailFromId}
              handleCellChange={handleRepartiCellChange}
              onClickAssociaResponsabili={onClickAssociaResponsabili}
              onRemoveRowReparti={onRemoveRowReparti}
              handleSave={handleSaveReparti}
              loading={isLoading}
              isEditingDisabledForConsulente={isEditingDisabledForConsulente}
              onToggleEditingForConsulente={onToggleEditingForConsulente}
            />
          </SectionBlockerWrapper>
          <SectionBlockerWrapper
            blocked={Boolean(
              activeSection && activeSection !== 'descrizioneReparti',
            )}
            showMessage={!isLoading}
          >
            <DescrizioneRepartiTable
              addNewRow={addNewRow}
              descrizioneReparti={descrizioneReparti}
              elencoReparti={elencoReparti}
              handleCambioRepartoInLocale={handleCambioRepartoInLocale}
              handleCellChange={handleLocaliCellChange}
              onClickAttrezzature={onClickAttrezzature}
              onRemoveRowDescrizioneReparti={onRemoveRowDescrizioneReparti}
              handleSave={handleSaveLocali}
              loading={isLoading}
              isEditingDisabledForConsulente={isEditingDisabledForConsulente}
              onToggleEditingForConsulente={onToggleEditingForConsulente}
            />
          </SectionBlockerWrapper>
          <SectionBlockerWrapper
            blocked={Boolean(
              activeSection && activeSection !== 'procedureAutocontrollo',
            )}
            showMessage={!isLoading}
          >
            <ProcedureAutocontrolloTable
              consulenteId={aggiornamentoGestioneOperativaDTO?.consulenteId}
              esercizioId={esercizioId}
              aggiornamentiGestioneOperativaId={
                aggiornamentoGestioneOperativaDTO.id
              }
              addNewRow={addNewRow}
              handleCellChange={handleProcedureCellChange}
              onRemoveRowProcedure={onRemoveRowProcedure}
              procedureAutocontrollo={procedureAutocontrollo}
              handleSave={handleSaveProcedure}
              loading={isLoading}
              isEditingDisabledForConsulente={isEditingDisabledForConsulente}
              onToggleEditingForConsulente={onToggleEditingForConsulente}
            />
          </SectionBlockerWrapper>

          {/* The non-empty queue makes itself noticed here, where the manual gets
              published, and not only inside the «Assegnazioni IoT» tab: it is the
              publication that produces the voci, and whoever publishes is the only
              one who knows what changed. It follows the IoT module, like the tab. */}
          {mostraCodaRevisione && (
            <AvvisoCodaRevisione
              esercizioId={esercizioId}
              ricaricaToken={codaRicaricaToken}
              onApriCoda={onApriCodaRevisione}
            />
          )}

          <div className={classes.buttonWrapper}>
            <ActionButton
              secondaryButton
              label="Vedi cronologia aggiornamenti"
              onClick={onClickCronologia}
              disabled={isLoading}
            />
            <ActionButton
              label="Replica su altri esercizi"
              onClick={() => runControlliCheck('replica')}
              disabled={
                isPubblicaDisabled || isLoading || activeSection !== null
              }
            />
            <ActionButton
              label="Rendi operative le modifiche sull'app"
              onClick={() => runControlliCheck('pubblica')}
              disabled={
                isPubblicaDisabled || isLoading || activeSection !== null
              }
            />
          </div>
        </Paper>
        <Paper elevation={3} className={classes.sectionContainer}>
          <SwitchSection
            disabledForm={false}
            multiselect={aggiornamentoGestioneOperativaDTO.multiselect ?? false}
            notificheAggregate={
              aggiornamentoGestioneOperativaDTO.notificheAggregate ?? false
            }
            onChange={onChangeCheckbox}
            showSaveButton={true}
            onSave={onSaveSwitchSettings}
            loadingDatiEsercizio={loadingDatiEsercizio}
            checkboxSaveSuccess={checkboxSaveSuccess}
            isLoadingGestioneOperativa={isLoading}
          />
        </Paper>
        <div className={classes.indietroButton}>
          <IndietroButton
            isDisabled={isLoading}
            alert={hasPendingChanges}
            onConfirmedNavigate={skipNextNavigationGuard}
          />
        </div>
      </div>
      <CustomSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
      {isModalResponsabiliOpen && (
        <ModalResponsabiliPerReparto
          open={isModalResponsabiliOpen}
          onClose={onCloseModalResponsabili}
          reparto={selectedReparto}
          responsabiliEsercizio={responsabiliSicurezza}
          onSubmit={onResponsabileSelected}
        />
      )}
      {isModalEserciziOpen && (
        <ModalSelezioneEserciziCommerciali
          esercizioId={esercizioId}
          open={isModalEserciziOpen}
          onClose={onCloseModalEsercizi}
          openSubmit={onReplicaAggiornamento}
          gestioneOperativaSeparata={aggiornamentoGestioneOperativaDTO.id}
        />
      )}
      {isAttrezzatureModalOpen && (
        <SelezioneAttrezzatureModal
          open={isAttrezzatureModalOpen}
          onClose={onCloseAttrezzatureModal}
          gestioneOperativaId={aggiornamentoGestioneOperativaDTO.id}
          selectedLocale={selectedLocale}
          setSelectedLocale={setSelectedLocale}
          onSave={saveAttrezzatureLocale}
          onLocaliRefetch={getLocaliData}
        />
      )}
      {confirmRemoveLocale.open && (
        <DialogConfirmOperation
          open={confirmRemoveLocale.open}
          title="Conferma eliminazione locale"
          description="Attenzione! Eliminando il locale si eliminineranno anche tutte le associazioni presenti nei controlli conformità. Vuoi continuare?"
          cancelButtonLabel="Annulla"
          confirmButtonLabel="Conferma"
          onCancel={handleCancelRemoveLocale}
          onConfirm={handleConfirmRemoveLocale}
        />
      )}
      {confirmRemoveReparto.open && (
        <DialogConfirmOperation
          open={confirmRemoveReparto.open}
          title="Eliminare il reparto?"
          description={`Il reparto "${confirmRemoveReparto.repartoNome}" ha dei locali collegati. Eliminandolo verranno eliminati anche i locali collegati e tutte le associazioni presenti nei controlli conformità.`}
          descriptionExtra="Questa operazione non può essere annullata."
          cancelButtonLabel="Annulla"
          confirmButtonLabel="Elimina reparto e locali"
          onCancel={handleCancelRemoveReparto}
          onConfirm={handleConfirmRemoveReparto}
        />
      )}
      {isModalSelezioneModalitaAutocontrolloOpen && (
        <ModalSelezioneModalitaAutocontrollo
          open={isModalSelezioneModalitaAutocontrolloOpen}
          onClose={onCloseModalSelezioneModalitaAutocontrollo}
          handleNewRow={handleNewRowProcedureAutocontrollo}
        />
      )}
      {isModalControlliOpen && (
        <ModalAvvisoControlliConformita
          open={isModalControlliOpen}
          onClose={onCloseModalControlli}
          controlliSenzaAttrezzature={controlliSenzaAttrezzature}
          onConfirm={onConfirmModalControlli}
        />
      )}
      {isModalCronologiaOpen && (
        <ModalCronologiaAggiornamenti
          open={isModalCronologiaOpen}
          onClose={onCloseModalCronologia}
          storicoAggiornamenti={storicoAggiornamenti}
          downloadPdf={dowloadPdf}
          loading={isLoadingCronologia}
        />
      )}
      {isImportHaccpOpen && (
        <ModalImportHaccpAgent
          {...importHaccpAgentBackend(aggiornamentoGestioneOperativaDTO.id)}
          // A write whose answer never came back may have replaced the
          // configuration all the same: the page reads it again, or the next
          // save of a section would send its old list over the import.
          onClose={({ scritturaIncerta }) => {
            setIsImportHaccpOpen(false);
            if (scritturaIncerta) ricaricaDopoImport();
          }}
          // The import replaced the whole draft, which is now there to
          // publish like any other edit.
          onImportato={() => {
            setIsImportHaccpOpen(false);
            setIsPubblicaDisabled(false);
            ricaricaDopoImport();
          }}
        />
      )}
    </div>
  );
};

GestioneOperativa.propTypes = {
  aggiornamentoGestioneOperativaDTO: PropTypes.object.isRequired,
  checkboxSaveSuccess: PropTypes.bool.isRequired,
  // Bumped by the owner of the screen on every write to the iot review queue, so
  // the banner below re-reads its count.
  codaRicaricaToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  esercizioId: PropTypes.string.isRequired,
  loadingDatiEsercizio: PropTypes.bool.isRequired,
  // Follows the esercizio's IoT module, like the «Assegnazioni IoT» tab: it is a
  // module switch, not a role check.
  mostraCodaRevisione: PropTypes.bool,
  onApriCodaRevisione: PropTypes.func,
  onCodaCambiata: PropTypes.func,
  onChangeCheckbox: PropTypes.func.isRequired,
  onPendingChangesChange: PropTypes.func,
  onSaveSwitchSettings: PropTypes.func.isRequired,
};

export default GestioneOperativa;
